import { useMemo, useState } from 'react';

import { Button, Field, FieldLabel, Input, Panel } from '../../design-system/index.js';
import {
  isValidCadenceSettings,
  type CadenceProgress,
  type CadenceSettings,
} from './cadence-runner.js';
import { useBulkCreateCadence, type BulkCreateCadenceStatus } from './bulk-create-controller.js';
import { CreatePage } from './create-page.js';

type CadenceDraft = Record<keyof CadenceSettings, string>;

const DEFAULT_CADENCE_DRAFT: CadenceDraft = {
  clicksPerLoop: '3',
  secondsBetweenClicks: '2',
  minutesBetweenLoops: '3',
  numberOfLoops: '60',
};

function draftNumber(value: string): number | null {
  if (!value.trim()) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function parseCadenceDraft(draft: CadenceDraft): CadenceSettings | null {
  const clicksPerLoop = draftNumber(draft.clicksPerLoop);
  const secondsBetweenClicks = draftNumber(draft.secondsBetweenClicks);
  const minutesBetweenLoops = draftNumber(draft.minutesBetweenLoops);
  const numberOfLoops = draftNumber(draft.numberOfLoops);
  if (
    clicksPerLoop === null ||
    secondsBetweenClicks === null ||
    minutesBetweenLoops === null ||
    numberOfLoops === null
  ) {
    return null;
  }

  const settings: CadenceSettings = {
    clicksPerLoop,
    secondsBetweenClicks,
    minutesBetweenLoops,
    numberOfLoops,
  };
  return isValidCadenceSettings(settings) ? settings : null;
}

function statusText(status: BulkCreateCadenceStatus, progress: CadenceProgress | null): string {
  if (status === 'running') {
    return progress
      ? `Running — loop ${progress.loopNumber}/${progress.totalLoops}, attempt ${progress.clickNumber}/${progress.clicksPerLoop}.`
      : 'Running — waiting for the first attempt.';
  }
  if (status === 'completed') return 'Cadence completed.';
  if (status === 'stopped') return 'Cadence stopped.';
  if (status === 'failed') return 'Cadence stopped because of an unexpected error.';
  return 'Cadence not running.';
}

export function BulkCreatePage() {
  const cadence = useBulkCreateCadence();
  const [draft, setDraft] = useState<CadenceDraft>(DEFAULT_CADENCE_DRAFT);
  const settings = useMemo(() => parseCadenceDraft(draft), [draft]);
  const running = cadence.status === 'running';

  const displayedDraft: CadenceDraft =
    running && cadence.runSettings
      ? {
          clicksPerLoop: String(cadence.runSettings.clicksPerLoop),
          secondsBetweenClicks: String(cadence.runSettings.secondsBetweenClicks),
          minutesBetweenLoops: String(cadence.runSettings.minutesBetweenLoops),
          numberOfLoops: String(cadence.runSettings.numberOfLoops),
        }
      : draft;

  const updateDraft = (field: keyof CadenceDraft, value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  return (
    <div className="grid gap-6">
      <CreatePage />

      <div className="mx-auto w-full max-w-4xl">
        <Panel className="grid gap-5">
          <div className="grid gap-1">
            <h2 className="text-lg font-semibold tracking-tight">Bulk create cadence</h2>
            <p className="text-sm text-ink-2">
              Runs continue during normal in-app navigation. Refreshing or closing the browser stops
              the run.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field>
              <FieldLabel htmlFor="bulk-clicks-per-loop">Clicks per loop</FieldLabel>
              <Input
                id="bulk-clicks-per-loop"
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                disabled={running}
                value={displayedDraft.clicksPerLoop}
                onChange={(event) => updateDraft('clicksPerLoop', event.currentTarget.value)}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="bulk-seconds-between-clicks">Seconds between clicks</FieldLabel>
              <Input
                id="bulk-seconds-between-clicks"
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                disabled={running}
                value={displayedDraft.secondsBetweenClicks}
                onChange={(event) => updateDraft('secondsBetweenClicks', event.currentTarget.value)}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="bulk-minutes-between-loops">Minutes between loops</FieldLabel>
              <Input
                id="bulk-minutes-between-loops"
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                disabled={running}
                value={displayedDraft.minutesBetweenLoops}
                onChange={(event) => updateDraft('minutesBetweenLoops', event.currentTarget.value)}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="bulk-number-of-loops">Number of loops</FieldLabel>
              <Input
                id="bulk-number-of-loops"
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                disabled={running}
                value={displayedDraft.numberOfLoops}
                onChange={(event) => updateDraft('numberOfLoops', event.currentTarget.value)}
              />
            </Field>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              disabled={running || settings === null}
              onClick={() => {
                if (settings) cadence.start(settings);
              }}
            >
              Start
            </Button>
            <Button type="button" variant="secondary" disabled={!running} onClick={cadence.stop}>
              Stop
            </Button>
            <p className="text-sm text-ink-2" role="status" aria-live="polite">
              {statusText(cadence.status, cadence.progress)}
            </p>
          </div>
        </Panel>
      </div>
    </div>
  );
}
